import React, { useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowRight, Loader2 } from "lucide-react";
import { fmtQty, type StockUnitRecord, type WarehouseRecord } from "@/types/inventory";

const field = "w-full h-10 rounded-lg border border-input bg-background px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring";

interface Props {
  open: boolean;
  units: StockUnitRecord[];
  onClose: () => void;
  onSaved: () => void;
}

/** Move the selected rolls (all from one warehouse) to another warehouse */
export function TransferDialog({ open, units, onClose, onSaved }: Props) {
  const { success, error } = useToast();
  const [warehouses, setWarehouses] = useState<WarehouseRecord[]>([]);
  const [to, setTo] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const fromIds = [...new Set(units.map((u) => u.warehouse_id))];
  const from = fromIds.length === 1 ? fromIds[0]! : "";

  useEffect(() => {
    if (!open) return;
    setTo("");
    setLocation("");
    setNotes("");
    api.get("/api/Warehouse/List").then((r) => setWarehouses(unwrapArray<WarehouseRecord>(r))).catch(() => setWarehouses([]));
  }, [open]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!from) return error("Validation Error", "The selected rolls are in different warehouses. Move one warehouse's rolls at a time.");
    if (!to) return error("Validation Error", "Choose the warehouse to move them to");
    setSaving(true);
    try {
      await api.post("/api/Inventory/Transfer", { from_warehouse_id: from, to_warehouse_id: to, unit_ids: units.map((u) => u.id), location: location.trim() || undefined, notes: notes.trim() || undefined });
      success("Stock moved", `${units.length} roll${units.length === 1 ? "" : "s"} transferred`);
      onSaved();
      onClose();
    } catch (err: any) {
      error("Could not move", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  const fromName = units[0]?.warehouse?.name || "";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Move to another warehouse</DialogTitle>
          <DialogDescription>
            {units.length} roll{units.length === 1 ? "" : "s"} selected{from ? ` in ${fromName}` : ""}.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2 text-sm">
          {!from && <p className="text-xs text-destructive">The selected rolls are in different warehouses. Select rolls from one warehouse at a time.</p>}
          <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
            <div className="space-y-1.5">
              <Label>From</Label>
              <div className="h-10 rounded-lg border border-border bg-muted/40 px-3 flex items-center text-sm">{fromName || "-"}</div>
            </div>
            <ArrowRight className="size-4 mb-3 text-muted-foreground" />
            <div className="space-y-1.5">
              <Label>To *</Label>
              <select className={field} value={to} onChange={(e) => setTo(e.target.value)}>
                <option value="">-- Choose --</option>
                {warehouses.filter((w) => w.id !== from).map((w) => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Rack / bin in the new warehouse</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} className="h-10" />
          </div>
          <div className="max-h-40 overflow-y-auto rounded-lg border border-border divide-y divide-border text-xs">
            {units.map((u) => (
              <div key={u.id} className="px-3 py-1.5 flex justify-between gap-3">
                <span>Roll {u.unit_no} · {u.product?.name}{u.variant ? ` · ${u.variant.name}` : ""} · Lot {u.lot?.lot_number}</span>
                <span className="text-muted-foreground whitespace-nowrap">{fmtQty(u.quantity, u.unit)}</span>
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving || !from} className="gap-1.5">
              {saving && <Loader2 className="size-4 animate-spin" />}
              Move rolls
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
